import { Stack, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { TooltipContentType } from 'enums';
import { GrammarReplacement, Paraphrase } from 'features/augmented-writing/augmented-writing.types';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import GrammarContent from '../suggestions/grammar-content';
import ParaphraseContent from '../suggestions/paraphrase-content';
import { ClickedHighlightState } from './custom-quill-editor';

interface Props {
  clickedHighlightState: ClickedHighlightState;

  insertAtPosition(word: string, pos: number | number[]): () => void;

  skipSuggestion(content: any, id: string): () => void;

  ignoreSuggestion(content: any, id: string): () => void;
}

const InteractiveTooltip: React.FC<Props> = ({ clickedHighlightState, insertAtPosition, skipSuggestion, ignoreSuggestion }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const popperRef = useRef<any>(null);

  const contentType = clickedHighlightState.contentType;
  const content = clickedHighlightState.content;

  const highlightId = `${contentType}-${content?.ranking_index}`;

  return (
    <Tooltip
      classes={{ tooltip: classes.tooltip }}
      open={clickedHighlightState.isOpen}
      PopperProps={{
        popperRef,
        anchorEl: clickedHighlightState.element,
      }}
      title={
        <>
          <Typography className={classes.title}>
            {contentType === TooltipContentType.GRAMMAR_REPLACEMENT ? t('grammarOptimization') : t('paiMatchOptimization')}
          </Typography>
          <div className={classes.content}>
            {contentType === TooltipContentType.GRAMMAR_REPLACEMENT ? (
              <GrammarContent grammarReplacement={content as GrammarReplacement} active={true} insertAtPosition={insertAtPosition} />
            ) : (
              <ParaphraseContent paraphrase={content as Paraphrase} active={true} insertAtPosition={insertAtPosition} />
            )}
          </div>
          <Stack className={classes.actions} direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
            <PaiLink text={t('ignoreOnce')} size={'xs'} underline={'always'} action={skipSuggestion(content, highlightId)} />
            <PaiLink text={t('alwaysIgnore')} size={'xs'} underline={'always'} action={ignoreSuggestion(content, highlightId)} />
          </Stack>
        </>
      }
    >
      <span />
    </Tooltip>
  );
};

const useStyles = makeStyles()((theme) => ({
  highlightToolTipText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.primary,
  },
  tooltip: {
    borderRadius: 0,
    color: 'black',
    background: 'white',
    boxShadow: Shadows.GREY_SHADOW_LIGHTER,
    maxWidth: '275px',
    padding: 0,
  },
  title: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    paddingTop: '8px',
  },
  actions: {
    padding: '4px',
  },
  content: {
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
}));

export default InteractiveTooltip;
